class CollapsibleHeader extends Collapsible {
  constructor() {
    super();
    this.header = this.closest('[data-header]');
    this.headerWrapper = this.closest('.shopify-section-header');
    if (this.header) {
      this.shadow = this.header.querySelector('.header-shadow');

      // extra events
      this.shadow?.addEventListener('click', this.closeAll.bind(this));
    }

    // set the redirect url in the Login form to be the same as the current url the user is visiting before form submission - a fallback one is set in liquid, but this JS one assures that any links with ? , for example, are also taken into account
    if (this.header.querySelector('[data-return-after-login]')) {
      const loginForms = document.querySelectorAll(['#customer_login_loyalty', '#customer_login_nav']);
      loginForms.forEach((loginForm) => {
        loginForm.addEventListener('submit', (event) => {
          event.preventDefault();
          loginForm.querySelector('[data-return-after-login]').value = window.location.href;
          loginForm.submit();
        });
      });
    }
  }

  events() {
    if (!this.header) {
      super.events();
    }
  }

  open(group) {
    super.open(group);
    if (this.headerWrapper) {
      this.headerWrapper.classList.add('shopify-section-header--open');
    }
    this.toggleHeaderClass(true);
  }

  close(group) {
    super.close(group);
    if (this.headerWrapper) {
      this.headerWrapper.classList.remove('shopify-section-header--open');
    }
    this.toggleHeaderClass(false);
  }

  closeSiblings(currentGroup = null) {
    super.closeSiblings(currentGroup);
    this.toggleHeaderClass(false);
  }

  openAll() {
    super.openAll();
    this.toggleHeaderClass(true);
  }

  closeAll() {
    super.closeAll();
    this.toggleHeaderClass(false);
  }

  toggleHeaderClass(open = true) {
    if (!this.header) {
      return false;
    }

    if (open) {
      // set header open
      this.header.classList.add('header--is-open');
    } else {
      // set header closed if all menu items are closed
      let close = true;
      this.groups.forEach((group) => {
        if (group.classList.contains(this.options.toggleClass)) {
          close = false;
        }
      });
      if (close) {
        this.header.classList.remove('header--is-open');
      }
    }
  }
}

class ToggleMobileNavigation extends ToggleElementClass {
  constructor() {
    super();
    this.body = this.closest('body');
    this.header = this.closest('[data-header]');
    this.headerWrapper = this.closest('.shopify-section-header');
    if (this.header) {
      this.shadow = this.header.querySelector('.header-shadow');

      window.addEventListener(
        'resize',
        debounce(() => {
          this.setViewportHeight();
        }, 100).bind(this)
      );

      // extra events
      document.addEventListener('keyup', this.onKeyUp.bind(this));
      this.shadow?.addEventListener('click', this.remove.bind(this));
    }
  }

  events() {
    if (!this.header) {
      super.events();
    }
  }

  setViewportHeight() {
    document.documentElement.style.setProperty('--viewport-height', `${window.innerHeight}px`);
    // update the viewport height when the announcement is in view
    const announcementBar = document.querySelector('#shopify-section-announcement-bar');
    if (announcementBar && !this.headerWrapper.classList.contains('shopify-section-header--sticky')) {
      const newViewportHeight = window.innerHeight - announcementBar.offsetHeight;
      document.documentElement.style.setProperty('--viewport-height', `${newViewportHeight}px`);
    }
  }

  onKeyUp(event) {
    if (!this.header.classList.contains('header--is-open')) {
      return;
    }
    if (event?.code.toUpperCase() !== 'ESCAPE') {
      return;
    }
    const { toggleClass } = this.options;
    if (this.target?.classList.contains(toggleClass)) {
      this.remove();
    }
  }

  add() {
    super.add();

    this.setViewportHeight();

    if (this.header) {
      this.header.classList.add('header--is-open');
    }
    if (this.headerWrapper) {
      this.headerWrapper.classList.add('shopify-section-header--open');
    }

    this.body.classList.add('menu-drawer--is-open');

    trapFocus(this.header);

    this.ariaExpanded = true;
  }

  remove() {
    super.remove();
    if (this.header) {
      this.header.classList.remove('header--is-open');
    }
    if (this.headerWrapper) {
      this.headerWrapper.classList.remove('shopify-section-header--mobile-open');
    }

    if (this.body.classList.contains('menu-drawer--is-open')) {
      this.body.classList.remove('menu-drawer--is-open');
      hideModal(this.target);
    }

    this.closeAllSiblings();
    removeTrapFocus();

    this.headerWrapper.querySelector('[data-menu-hamburger]').ariaExpanded = false;
  }

  closeAllSiblings() {
    const collapsibles = this.target.querySelector('collapsible-element');
    collapsibles.closeAll();
  }
}

class StickyHeader extends HTMLElement {
  constructor() {
    super();

    // select elements
    this.body = this.closest('body');
    this.header = document.querySelector('[data-header]');
    this.headerWrapper = document.querySelector('.shopify-section-header');
    this.announcementBar = document.querySelector('#shopify-section-announcement-bar');
    this.headerElement = this;
    this.headerNav = this.querySelector('collapsible-header');
    this.mobileNav = this.querySelector('toggle-mobile-navigation');
    this.searchDropdownComponent = document.querySelector('search-dropdown');

    if (this.headerWrapper) {
      // scroll parameters
      this.currentScrollTop = 0;
      this.scrollOffset = 0;
      this.headerStart = 0;

      if (this.announcementBar) {
        const announcementBarHeight = this.announcementBar.offsetHeight;
        this.scrollOffset += announcementBarHeight;
        this.headerStart += announcementBarHeight;
      }

      if (this.headerElement) {
        this.scrollOffset += this.headerElement.offsetHeight;
      }

      // scroll event
      this.raf = null; // Safe request animation frame
      (this.timer = null), // Safe scroll timer
        window.addEventListener(
          'scroll',
          () => {
            clearTimeout(this.timer);
            if (!this.raf) {
              // A request animation frame is used to animate on max 60fps.
              this.raf = requestAnimationFrame(this.scrollAnimation.bind(this));
            }
            this.timer = setTimeout(() => {
              // Stop animation, cancel request animation frame
              cancelAnimationFrame(this.raf);
              this.raf = null;
            }, 50);
          },
          false
        );
    }
  }

  scrollAnimation() {
    const scrollTop = window.pageYOffset || document.documentElement.scrollTop;
    const isSafari = navigator.userAgent.indexOf('Chrome') === -1 && navigator.userAgent.indexOf('Safari') > -1;

    // only animate if header is closed.
    if (!this.header?.classList?.contains('header--is-open')) {
      if (scrollTop + window.innerHeight > document.body.clientHeight) {
        this.hide();
      } else if (scrollTop > this.currentScrollTop && scrollTop > this.scrollOffset) {
        this.hide();
      } else if (scrollTop <= this.headerStart && !isSafari) {
        // remove the sticky & animate classes so the header has position relative from the top scroll
        // not in Safari, since it generates a bug in the elements positioning
        this.headerWrapper.classList.remove('shopify-section-header--sticky', 'shopify-section-header--animate');
      } else if (scrollTop < this.currentScrollTop && scrollTop > this.headerStart) {
        this.reveal();
      }
    }

    this.currentScrollTop = scrollTop;

    // Recall request animation loop
    this.raf = requestAnimationFrame(this.scrollAnimation.bind(this));
  }

  hide() {
    // Guard: don't hide if the search dropdown is open
    if (this.searchDropdownComponent && this.searchDropdownComponent.visibility) return;
    // Don't hide the desktop header - Only Homepage
    if (this.body.classList.contains('template-index') && window.innerWidth > 1024) return;
    // Remove body class when the header is hidden - Only collection & product templates
    if (this.body.classList.contains('template-collection') || this.body.classList.contains('template-product')) {
      this.body.classList.remove('header--is-revealed');
      if (this.currentScrollTop >= window.innerHeight) {
        this.body.classList.add('scrolling-page');
      }
    }
    this.headerWrapper.classList.add('shopify-section-header--hidden', 'shopify-section-header--sticky');
    this.closeHeaderNav();
    this.closeMobileNav();
  }

  reveal() {
    this.headerWrapper.classList.add('shopify-section-header--sticky', 'shopify-section-header--animate');
    this.headerWrapper.classList.remove('shopify-section-header--hidden');
    // Set body class when the header is visible - Only collection & product templates
    if (this.body.classList.contains('template-collection') || this.body.classList.contains('template-product')) {
      this.body.classList.add('header--is-revealed');
      if (this.currentScrollTop < window.innerHeight) {
        this.body.classList.remove('scrolling-page');
      }
    }
  }

  closeHeaderNav() {
    if (!this.headerNav || window.innerWidth < 768) {
      return false;
    }
    this.headerNav.closeAll();
  }

  closeMobileNav() {
    if (!this.mobileNav) {
      return false;
    }
    this.mobileNav.remove();
  }
}

if (!customElements.get('collapsible-header')) {
  customElements.define('collapsible-header', CollapsibleHeader);
}

if (!customElements.get('toggle-mobile-navigation')) {
  customElements.define('toggle-mobile-navigation', ToggleMobileNavigation);
}

if (!customElements.get('sticky-header')) {
  customElements.define('sticky-header', StickyHeader);
}
